import BigNumber from "bignumber.js";

import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { getCurrentLocale } from "Infrastructure/Utils/LocaleUtils";

export const formatCurrency = (
  value: number | null | undefined,
  currencyCode: CurrencyCode | string | null | undefined,
  decimalPlaces = 2,
) => {
  const locales = () => {
    return getCurrentLocale();
  };

  const formatter = new Intl.NumberFormat(locales(), {
    style: "currency",
    currency: currencyCode ?? CurrencyCode.EUR,
    maximumFractionDigits: decimalPlaces,
    minimumFractionDigits: 0,
  });
  return formatter.format(value ?? 0);
};

export const getCurrencySymbol = (currency?: CurrencyCode) => {
  if (currency) {
    return formatCurrency(0, currency).replace(/\d/g, "").trim();
  }

  return currency;
};

export function roundHalfUp(value: number) {
  return new BigNumber(value)
    .decimalPlaces(2, BigNumber.ROUND_HALF_UP)
    .toNumber();
}

export interface PriceConversionResult {
  primary: string;
  secondary: string | null;
  tooltipText: string | null;
}

export function formatPriceWithConversion(
  eurAmount: number | null | undefined,
  userCurrency: string,
  rate: number | undefined,
  rateDate: string | undefined,
  decimalPlaces = 2,
): PriceConversionResult {
  const primary = formatCurrency(eurAmount, CurrencyCode.EUR, decimalPlaces);

  if (
    eurAmount == null ||
    userCurrency === CurrencyCode.EUR ||
    rate === undefined
  ) {
    return { primary, secondary: null, tooltipText: null };
  }

  const converted = eurAmount * rate;
  const secondary = formatCurrency(converted, userCurrency, decimalPlaces);

  const formattedRate = new Intl.NumberFormat(getCurrentLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(rate);

  const dateFormatted = rateDate
    ? new Intl.DateTimeFormat(getCurrentLocale()).format(new Date(rateDate))
    : "";

  const tooltipText = `1 EUR = ${formattedRate} ${userCurrency}${dateFormatted ? ` (${dateFormatted})` : ""}`;

  return { primary, secondary, tooltipText };
}
